import React, { useEffect, useState, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
  CartesianGrid, ReferenceLine,
} from 'recharts';
import { analyticsAPI, workforceAPI } from '../../services/api';
import Skeleton from '../../components/common/Skeleton';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  T, DONUT_COLORS, FONT, CARD, I, greeting,
  ChartTip, FadeSection, HeroStat, KpiCard, ActionLink,
  AlertCard, ChartCard, ChartEmpty, PolarStyles,
} from './shared';

// ═══ Admin / Operations Dashboard (original "Benevita Polar" view) ═════════════
export default function AdminDashboard() {
  const [stats, setStats]                       = useState(null);
  const [visitsTrend, setVisitsTrend]           = useState([]);
  const [serviceBreakdown, setServiceBreakdown] = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [activeDay, setActiveDay]               = useState(null); // click-to-filter
  const [creds, setCreds]                       = useState(null); // license expiry (M4)
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || 'there';

  const load = useCallback(({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    Promise.allSettled([
      analyticsAPI.dashboard(),
      analyticsAPI.visitsTrend(7),
      analyticsAPI.serviceBreakdown(),
      workforceAPI.credentialsExpiring(90),
    ]).then(([d, v, s, c]) => {
      setStats(d.status === 'fulfilled' ? (d.value?.data || d.value || {}) : {});
      if (v.status === 'fulfilled') setVisitsTrend(v.value?.data || []);
      if (s.status === 'fulfilled') setServiceBreakdown(s.value?.data || []);
      if (c.status === 'fulfilled') setCreds(c.value?.data || null);
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(); }, [load]);
  // Dashboard stays live — new requests/visits from the field bump the
  // counters without a manual reload (30s cadence; heavier than list pages).
  useAutoRefresh(useCallback(() => load({ silent: true }), [load]), 30000);

  // ── Derived urgency alerts (logic preserved) ───────────────────────────────
  const criticalRequests = stats?.critical_requests ?? Math.min(5, stats?.active_requests ?? 0);
  const overdueVisits    = stats?.overdue_visits    ?? 0;
  const pendingReferrals = stats?.pending_referrals ?? stats?.total_referrals ?? 0;

  const urgencyAlerts = [
    criticalRequests > 0 && {
      tier: 'critical',
      message: `${criticalRequests} care request${criticalRequests !== 1 ? 's' : ''} need immediate nurse assignment`,
      actionLabel: 'Assign Now',
      onAction: () => navigate('/dispatch'),
    },
    overdueVisits > 0 && {
      tier: 'warning',
      message: `${overdueVisits} scheduled visit${overdueVisits !== 1 ? 's' : ''} are overdue — nurses have not checked in`,
      actionLabel: 'View Visits',
      onAction: () => navigate('/visits'),
    },
    pendingReferrals > 0 && {
      tier: 'info',
      message: `${pendingReferrals} new doctor referral${pendingReferrals !== 1 ? 's' : ''} pending clinical review`,
      actionLabel: 'Review',
      onAction: () => navigate('/referrals'),
    },
  ].filter(Boolean);

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ width: '100%', fontFamily: FONT }}>
      <div style={{
        ...CARD, borderRadius: 20, padding: '26px 28px', marginBottom: 18,
        background: 'linear-gradient(135deg,#2B1B22 0%,#7B1E3C 70%,#94335A 100%)',
        border: 'none',
      }}>
        <Skeleton width={260} height={24} style={{ marginBottom: 10 }} />
        <Skeleton width={380} height={13} />
      </div>
      <div style={{ ...CARD, padding: '14px 18px', marginBottom: 18 }}>
        {[1, 2].map(i => <Skeleton key={i} width="90%" height={14} style={{ marginBottom: 8 }} />)}
      </div>
      <Skeleton.KPIGrid count={8} cols={4} />
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        <Skeleton.Card rows={3} />
        <Skeleton.Card rows={3} />
      </div>
    </div>
  );

  // ── KPI definitions (data preserved) ──────────────────────────────────────
  const kpis = [
    {
      title: 'Active Requests',
      value: stats?.active_requests ?? 0,
      color: T.danger,
      Icon:  I.bolt,
      sub:   criticalRequests > 0 ? `${criticalRequests} critical` : 'None critical',
      delta: stats?.active_requests > 0 ? `${stats.active_requests} open` : 'No change',
      deltaDir: stats?.active_requests > 0 ? 'down' : 'neutral',
      action: <ActionLink label="Open Dispatch" color={T.danger} onClick={() => navigate('/dispatch')} />,
    },
    {
      title: 'Total Patients',
      value: stats?.total_patients ?? 0,
      color: T.primary,
      Icon:  I.heart,
      sub:   'registered',
      delta: '3 this week',
      deltaDir: 'up',
      action: <ActionLink label="View Patients" onClick={() => navigate('/ss/parties?role_code=PATIENT')} />,
    },
    {
      title: 'Active Nurses',
      value: stats?.available_nurses ?? stats?.total_nurses ?? 0,
      color: T.teal,
      Icon:  I.stetho,
      sub:   `of ${stats?.total_nurses ?? 0} total`,
      delta: stats?.available_nurses > 0 ? 'Available' : 'Off duty',
      deltaDir: stats?.available_nurses > 0 ? 'up' : 'neutral',
      action: <ActionLink label="Nurse Registry" color={T.teal} onClick={() => navigate('/ss/nurses')} />,
    },
    {
      title: 'Visits Today',
      value: stats?.visits_today ?? 0,
      color: T.purple,
      Icon:  I.calendar,
      sub:   `${stats?.completed_visits ?? 0} completed`,
      delta: stats?.visits_today > 0 ? 'Active' : 'None scheduled',
      deltaDir: stats?.visits_today > 0 ? 'up' : 'neutral',
    },
    {
      title: 'Total Doctors',
      value: stats?.total_doctors ?? 0,
      color: T.info,
      Icon:  I.cross,
      action: <ActionLink label="Doctor Registry" color={T.info} onClick={() => navigate('/ss/doctors')} />,
    },
    {
      title: 'Completed Visits',
      value: stats?.completed_visits ?? 0,
      color: T.green,
      Icon:  I.check,
      delta: 'This month',
      deltaDir: 'up',
    },
    {
      title: 'Pending Billing',
      value: stats?.pending_billing ?? 0,
      color: T.warning,
      Icon:  I.coin,
      sub:   stats?.pending_billing > 0 ? 'Needs review' : undefined,
      action: stats?.pending_billing > 0
        ? <ActionLink label="Review Billing" color={T.warning} onClick={() => navigate('/billing')} />
        : undefined,
    },
    {
      title: 'Revenue (KES)',
      value: Number(stats?.total_revenue || 0),
      displayValue: Number(stats?.total_revenue || 0).toLocaleString(),
      color: T.primaryLight,
      Icon:  I.diamond,
      sub:   'total billed',
    },
  ];

  // ── Chart data (logic preserved) ──────────────────────────────────────────
  const EMPTY_TREND = [
    { day: 'Mon', count: 0 }, { day: 'Tue', count: 0 }, { day: 'Wed', count: 0 },
    { day: 'Thu', count: 0 }, { day: 'Fri', count: 0 }, { day: 'Sat', count: 0 },
    { day: 'Sun', count: 0 },
  ];
  const trendData = visitsTrend.length > 0 ? visitsTrend : EMPTY_TREND;
  const hasTrend  = visitsTrend.length > 0 && trendData.some(d => (d.count || 0) > 0);
  const avgVisits = trendData.reduce((s, d) => s + (d.count || 0), 0) / trendData.length;

  const handleBarClick = (data) => {
    setActiveDay(prev => prev === data?.activeLabel ? null : data?.activeLabel);
  };

  const filteredData = activeDay
    ? trendData.map(d => ({ ...d, _dimmed: (d.day || d.date) !== activeDay }))
    : trendData;

  const totalServices = serviceBreakdown.reduce((s, d) => s + (d.count || 0), 0);

  const quickActions = [
    { label: 'New Patient',    path: '/ss/parties?role_code=PATIENT', color: T.primary, Icon: I.user_plus },
    { label: 'Register Nurse', path: '/ss/nurses',                    color: T.teal,    Icon: I.nurse     },
    { label: 'Care Request',   path: '/care-requests',                color: T.warning, Icon: I.request   },
    { label: 'New Referral',   path: '/referrals',                    color: T.purple,  Icon: I.refer     },
    { label: 'Dispatch',       path: '/dispatch',                     color: T.danger,  Icon: I.dispatch  },
    { label: 'Analytics',      path: '/analytics',                    color: T.info,    Icon: I.chart     },
  ];

  const systemServices = [
    { label: 'Party Registry',       status: 'operational', color: T.teal },
    { label: 'Dispatch Engine',      status: 'operational', color: T.teal },
    { label: 'Visit Tracking',       status: 'operational', color: T.teal },
    { label: 'Billing Module',       status: 'operational', color: T.teal },
    { label: 'Notification Service', status: 'operational', color: T.teal },
  ];

  return (
    <div style={{ width: '100%', fontFamily: FONT, color: T.text }}>

      {/* ════ Hero / greeting band ════ */}
      <FadeSection index={0}>
        <div style={{
          position: 'relative', overflow: 'hidden',
          marginBottom: 18, padding: '26px 28px',
          background: 'linear-gradient(135deg,#2B1B22 0%,#7B1E3C 70%,#94335A 100%)',
          borderRadius: 20, color: '#fff',
          boxShadow: '0 14px 36px -14px rgba(45,16,32,.5)',
        }}>
          {/* Decorative radial glows */}
          <div aria-hidden style={{
            position: 'absolute', top: -120, right: -90,
            width: 340, height: 340, borderRadius: '50%',
            background: `radial-gradient(circle, ${T.sky}33 0%, transparent 70%)`,
            pointerEvents: 'none',
          }} />
          <div aria-hidden style={{
            position: 'absolute', bottom: -140, left: '32%',
            width: 320, height: 320, borderRadius: '50%',
            background: `radial-gradient(circle, ${T.teal}33 0%, transparent 70%)`,
            pointerEvents: 'none',
          }} />

          <div style={{
            position: 'relative', display: 'flex',
            justifyContent: 'space-between', alignItems: 'center',
            gap: 20, flexWrap: 'wrap',
          }}>
            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 10px', borderRadius: 999,
                background: 'rgba(217,140,179,.14)',
                border: '1px solid rgba(217,140,179,.3)',
                fontSize: 10.5, fontWeight: 600, color: '#7DD3FC',
                marginBottom: 10, letterSpacing: '.3px',
              }}>
                <I.spark size={11} color="#7DD3FC" />
                Live · {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                <span style={{ opacity: .55 }}>·</span>
                {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
              <h1 style={{ fontSize: 24, fontWeight: 800, margin: '0 0 6px', letterSpacing: '-.5px', color: '#fff' }}>
                {greeting()}, {firstName} 👋
              </h1>
              <p style={{ color: 'rgba(255,255,255,.66)', margin: 0, fontSize: 13, lineHeight: 1.5 }}>
                Here's what's happening across your care operations today.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <HeroStat label="Visits today" value={stats?.visits_today ?? 0} Icon={I.calendar} />
              <HeroStat label="Active requests" value={stats?.active_requests ?? 0} Icon={I.bolt} />
              <HeroStat label="Nurses on duty" value={stats?.available_nurses ?? stats?.total_nurses ?? 0} Icon={I.stetho} />
              <button
                onClick={load}
                title="Refresh dashboard data"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40,
                  background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)',
                  borderRadius: 12, color: '#fff', cursor: 'pointer',
                  transition: 'background .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,.22)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,.12)'; }}
              >
                <I.refresh size={15} color="#fff" />
              </button>
            </div>
          </div>
        </div>
      </FadeSection>

      {/* ════ Urgency alerts panel ════ */}
      {urgencyAlerts.length > 0 && (
        <FadeSection index={1} style={{ display: 'grid', gap: 10, marginBottom: 18 }}>
          {urgencyAlerts.map((a, i) => <AlertCard key={i} {...a} />)}
        </FadeSection>
      )}

      {/* ════ KPI grid ════ */}
      <FadeSection
        index={2}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 14, marginBottom: 18,
        }}
      >
        {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
      </FadeSection>

      {/* ════ Charts row ════ */}
      <FadeSection
        index={3}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 14, marginBottom: 18,
        }}
      >
        {/* Visit trend — gradient area */}
        <ChartCard
          title="Weekly Visit Trend"
          subtitle={activeDay ? `Filtered: ${activeDay} — click again to clear` : 'Home nursing visits · last 7 days · click to filter'}
          action={activeDay && (
            <button
              onClick={() => setActiveDay(null)}
              style={{
                fontSize: 10.5, padding: '5px 11px', display: 'inline-flex', alignItems: 'center', gap: 4,
                background: T.pageBg, border: `1px solid ${T.border}`, borderRadius: 8, cursor: 'pointer',
                color: T.muted, fontFamily: FONT, fontWeight: 600,
              }}
            >
              <I.x size={10} /> Clear filter
            </button>
          )}
        >
          {hasTrend ? (
            <>
              <ResponsiveContainer width="100%" height={230}>
                <AreaChart data={filteredData} onClick={handleBarClick} style={{ cursor: 'pointer' }}>
                  <defs>
                    <linearGradient id="bnvAreaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={T.primary} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={T.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} strokeDasharray="3 6" stroke="#E8EEF5" />
                  <XAxis
                    dataKey={visitsTrend.length > 0 ? 'date' : 'day'}
                    tickFormatter={v => v?.slice(-5) || v}
                    tick={{ fill: '#8A9BB0', fontSize: 11 }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#8A9BB0', fontSize: 11 }}
                    axisLine={false} tickLine={false} width={32}
                  />
                  <Tooltip
                    content={<ChartTip nameFormatter={() => 'Visits'} />}
                    cursor={{ stroke: T.border, strokeWidth: 1 }}
                  />
                  {avgVisits > 0 && (
                    <ReferenceLine
                      y={Math.round(avgVisits)}
                      stroke={T.warning} strokeDasharray="5 3" strokeWidth={1.5}
                      label={{ value: 'avg', position: 'right', fontSize: 9, fill: T.warning }}
                    />
                  )}
                  <Area
                    type="monotone" dataKey="count" name="Visits"
                    stroke={T.primary} strokeWidth={2.5}
                    fill="url(#bnvAreaGrad)"
                    dot={activeDay ? (props) => {
                      const { cx, cy, payload } = props;
                      return (
                        <circle
                          key={`dot-${cx}-${cy}`}
                          cx={cx} cy={cy} r={4}
                          fill={payload._dimmed ? T.border : T.primary}
                          stroke="#fff" strokeWidth={1.5}
                        />
                      );
                    } : false}
                    activeDot={{ r: 5, fill: T.primary, stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
              {avgVisits > 0 && (
                <div style={{ fontSize: 11, color: T.muted, marginTop: 8 }}>
                  <span style={{ color: T.warning }}>—</span> Average: {Math.round(avgVisits)} visits/day
                </div>
              )}
            </>
          ) : (
            <ChartEmpty
              color={T.primary} Icon={I.pulse}
              title="No visits in the past 7 days"
              caption="Visit trend will appear here once nurses begin checking in to scheduled visits."
            />
          )}
        </ChartCard>

        {/* Service mix — donut with center label and chip legend */}
        <ChartCard title="Service Mix" subtitle="Care requests by type">
          {serviceBreakdown.length > 0 ? (
            <>
              <div style={{ position: 'relative' }}>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={serviceBreakdown} cx="50%" cy="50%"
                      innerRadius="62%" outerRadius="92%"
                      dataKey="count" nameKey="service_type"
                      paddingAngle={2} cornerRadius={6}
                      stroke="none"
                    >
                      {serviceBreakdown.map((_, i) => (
                        <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<ChartTip nameFormatter={n => (n || '').replace(/_/g, ' ').toLowerCase()} />} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{
                  position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
                }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: T.text, lineHeight: 1 }}>
                    {totalServices.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '.6px', marginTop: 4 }}>
                    requests
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                {serviceBreakdown.map((d, i) => (
                  <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    fontSize: 11, fontWeight: 600, color: T.text,
                    background: T.pageBg, border: `1px solid ${T.border}`,
                    borderRadius: 999, padding: '4px 10px',
                  }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: DONUT_COLORS[i % DONUT_COLORS.length],
                    }} />
                    {(d.service_type || '').replace(/_/g, ' ').toLowerCase()}
                    <span style={{ color: T.muted, fontWeight: 700 }}>{d.count}</span>
                  </span>
                ))}
              </div>
            </>
          ) : (
            <ChartEmpty
              color={T.teal} Icon={I.filter}
              title="No care requests yet"
              caption="Service distribution appears as care requests are created."
            />
          )}
        </ChartCard>
      </FadeSection>

      {/* ════ Quick actions ════ */}
      <FadeSection index={4} style={{ marginBottom: 18 }}>
        <div style={{ ...CARD, padding: '20px 22px' }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: T.text,
            marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <I.bolt size={14} color={T.primary} /> Quick Actions
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 10,
          }}>
            {quickActions.map(({ label, path, color, Icon }) => (
              <button
                key={path}
                className="bnv-tile"
                onClick={() => navigate(path)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 11,
                  padding: '13px 15px',
                  background: '#fff', color: T.text,
                  border: `1px solid ${T.border}`,
                  borderRadius: 14, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: FONT,
                  textAlign: 'left', justifyContent: 'flex-start',
                }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 40, height: 40, borderRadius: 11,
                  background: `${color}14`, flexShrink: 0,
                }}>
                  <Icon size={18} color={color} />
                </span>
                {label}
              </button>
            ))}
          </div>
        </div>
      </FadeSection>

      {/* ════ System status — slim footer card ════ */}
      <FadeSection index={5} style={{ marginBottom: 18 }}>
        <div style={{
          ...CARD, padding: '14px 22px',
          display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 700, color: T.text }}>
            <I.shield size={14} color={T.teal} /> System Status
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            fontSize: 10.5, fontWeight: 700, color: T.teal,
            background: `${T.teal}14`, padding: '3px 10px', borderRadius: 999,
          }}>
            <span className="hhms-status-dot" style={{
              width: 6, height: 6, borderRadius: '50%', background: T.teal, display: 'inline-block',
            }} />
            All systems normal
          </span>
          <span aria-hidden style={{ width: 1, alignSelf: 'stretch', background: T.border }} />
          {systemServices.map(({ label, color }) => (
            <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: T.muted, fontWeight: 600 }}>
              <span className="hhms-status-dot" style={{
                width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block',
              }} />
              {label}
            </span>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 10.5, color: T.muted }}>
            Last sync: {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </FadeSection>

      {/* ════ Workforce credential expiry (M4) ════ */}
      {creds && (creds.expired > 0 || creds.expiring > 0 || creds.missing > 0) && (
        <FadeSection index={5}>
          <div style={{ ...CARD, padding: '18px 22px', marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 14.5, fontWeight: 800, color: T.navy }}>
                🪪 Workforce credentials — next {creds.window_days} days
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {creds.expired > 0 && <span style={{ fontSize: 11, fontWeight: 800, color: '#B91C1C', background: '#FEE2E2', borderRadius: 999, padding: '3px 10px' }}>{creds.expired} expired</span>}
                {creds.expiring > 0 && <span style={{ fontSize: 11, fontWeight: 800, color: '#92400E', background: '#FEF3C7', borderRadius: 999, padding: '3px 10px' }}>{creds.expiring} expiring</span>}
                {creds.missing > 0 && <span style={{ fontSize: 11, fontWeight: 800, color: '#475569', background: '#F1F5F9', borderRadius: 999, padding: '3px 10px' }}>{creds.missing} no expiry on file</span>}
              </div>
            </div>
            {(creds.items || []).slice(0, 6).map(i => (
              <div key={i.party_id + i.role} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #F1F5F9', fontSize: 12.5 }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                  background: i.status === 'EXPIRED' ? '#DC2626' : i.status === 'EXPIRING' ? '#F59E0B' : '#94A3B8' }} />
                <span style={{ fontWeight: 700, color: T.navy, minWidth: 160 }}>{i.name}</span>
                <span style={{ color: '#8b7681' }}>{i.role}</span>
                <span style={{ color: '#8b7681', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5 }}>{i.license_number}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 700,
                  color: i.status === 'EXPIRED' ? '#B91C1C' : i.status === 'EXPIRING' ? '#92400E' : '#8b7681' }}>
                  {i.status === 'EXPIRED' ? `expired ${-i.days_left}d ago`
                    : i.status === 'EXPIRING' ? `${i.days_left}d left (${i.license_expiry})`
                    : 'no expiry recorded'}
                </span>
              </div>
            ))}
            {(creds.items || []).length > 6 && (
              <div style={{ fontSize: 11.5, color: '#94A3B8', marginTop: 8 }}>
                +{creds.items.length - 6} more — keep licenses current to stay dispatchable.
              </div>
            )}
          </div>
        </FadeSection>
      )}

      {/* ════ Compliance bar ════ */}
      <FadeSection index={6}>
        <div style={{
          background: 'linear-gradient(135deg,#2B1B22 0%,#7b1e3c 100%)',
          borderRadius: 16, padding: '12px 22px',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          boxShadow: '0 10px 26px -14px rgba(45,16,32,.5)',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontSize: 10, fontWeight: 700, color: '#7AB2D8',
            letterSpacing: '1.2px', textTransform: 'uppercase',
            paddingRight: 12, borderRight: '1px solid #1E3A52',
          }}>
            <I.shield size={11} color="#7AB2D8" />
            Compliance
          </span>
          {['HL7 FHIR R4', 'HIPAA', 'GDPR', 'ISO 27001', 'ICD-10', 'LOINC', 'SNOMED CT', 'Kenya DPA'].map(s => (
            <span key={s} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 10.5, color: '#9BC4E2', fontWeight: 600,
              padding: '3px 9px', borderRadius: 999,
              background: 'rgba(217,140,179,.08)',
              border: '1px solid rgba(217,140,179,.18)',
            }}>
              <I.check size={9} color={T.sky} /> {s}
            </span>
          ))}
        </div>
      </FadeSection>

      <PolarStyles />
    </div>
  );
}
